import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { openPrivateFile } from "../lib/privatePhotos";
import {
  Scale, FileText, Receipt, FileSignature, IdCard, Banknote, Plus, Trash2,
  RefreshCw, ExternalLink, Sparkles, Copy, Download as DownloadIcon, Edit3,
  Search, Filter, X as XIcon,
} from "lucide-react";

const TYPES = [
  { v: "contract",   label: "Contracts",   icon: FileSignature, hex: "#14B8A6", desc: "Employment, collaborations, NDAs" },
  { v: "form",       label: "Consent Forms", icon: FileText,   hex: "#06B6D4", desc: "Model releases, platform verifications" },
  { v: "receipt",    label: "Receipts",    icon: Receipt,       hex: "#22c55e", desc: "Payment records, invoices" },
  { v: "id_document", label: "ID / Passport", icon: IdCard,    hex: "#a855f7", desc: "Identity verification documents" },
  { v: "tax",        label: "Tax",         icon: Banknote,      hex: "#f59e0b", desc: "Tax forms, declarations" },
  { v: "other",      label: "Other",       icon: FileText,      hex: "#71717a", desc: "Miscellaneous legal docs" },
];
const TYPE_HEX = Object.fromEntries(TYPES.map(t => [t.v, t.hex]));
const TYPE_LABEL = Object.fromEntries(TYPES.map(t => [t.v, t.label]));

const CONTRACT_TEMPLATES = [
  { v: "editor_hire",     label: "Editor Hire",       icon: Edit3 },
  { v: "cameraman_hire",  label: "Cameraman / DP",    icon: Edit3 },
  { v: "model_release",   label: "Model Release",     icon: FileSignature },
  { v: "studio_collab",   label: "Studio Collab",     icon: FileSignature },
  { v: "nda",             label: "NDA",               icon: FileSignature },
];

export default function Legal() {
  const [tab, setTab] = useState("contract");
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // doc being edited (or "new")
  const [generator, setGenerator] = useState(null); // contract generator state

  const load = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/api/legal/"),
        api.get("/api/legal/summary"),
      ]);
      setDocs(d.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return docs.filter(d => {
      if (d.doc_type !== tab) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(d.title?.toLowerCase().includes(q) ||
              d.counterparty?.toLowerCase().includes(q) ||
              (d.tags || []).some(t => t.toLowerCase().includes(q)))) return false;
      }
      return true;
    });
  }, [docs, tab, search]);

  const removeDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/api/legal/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <AppLayout title="Legal Vault" overline="📁 CONTRACTS · CONSENTS · RECEIPTS · COMPLIANCE"
      actions={
        <>
          <button onClick={() => setGenerator({ contract_type: "editor_hire" })} className="btn-primary inline-flex items-center gap-1.5 text-[12px]" data-testid="legal-gen-btn">
            <Sparkles size={13} /> Generate Contract
          </button>
          <button onClick={() => setEditing("new")} className="btn-ghost inline-flex items-center gap-1.5 text-[12px]" data-testid="legal-add-btn">
            <Plus size={13} /> Add document
          </button>
          <button onClick={load} className="btn-ghost inline-flex items-center gap-1.5 text-[12px]" data-testid="legal-refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </>
      }>
      
      {/* Enhanced Info Banner */}
      <div className="mb-6 p-4 rounded-lg border border-cyan-800/30 bg-gradient-to-r from-cyan-950/30 to-transparent">
        <div className="flex items-start gap-3">
          <Scale size={24} className="text-cyan-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-cyan-300 mb-1 text-base">Centralized Legal Document Management</div>
            <div className="text-zinc-400 text-xs leading-relaxed">
              Store, track, and manage all legal documents: contracts, model releases, consent forms, receipts, ID verifications, and tax documents. 
              Link documents to CRM contacts and videos for complete compliance tracking.
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Category Tabs with descriptions */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {TYPES.map(t => {
            const Icon = t.icon;
            const count = summary.by_type?.[t.v] || 0;
            return (
              <button key={t.v} onClick={() => setTab(t.v)} type="button"
                data-testid={`legal-tab-${t.v}`}
                className="bg-zinc-950 border rounded-lg p-4 text-left transition hover:-translate-y-1 hover:shadow-lg group"
                style={{
                  borderColor: tab === t.v ? t.hex : "var(--gio-border)",
                  boxShadow: tab === t.v ? `0 0 0 2px ${t.hex}20, 0 4px 12px ${t.hex}15` : undefined,
                  background: tab === t.v ? `linear-gradient(135deg, ${t.hex}10, transparent)` : undefined,
                }}>
                <div className="flex items-center justify-between mb-2">
                  <Icon size={16} style={{ color: t.hex }} className="group-hover:scale-110 transition" />
                  <span className="font-heading text-2xl font-bold" style={{ color: tab === t.v ? t.hex : "var(--gio-text)" }}>
                    {count}
                  </span>
                </div>
                <div className="text-xs font-bold mb-1" style={{ color: t.hex }}>{t.label}</div>
                <div className="text-[10px] text-zinc-500 leading-tight">{t.desc}</div>
              </button>
            );
          })}
        </div>
      )}

      {summary?.receipts_amount_by_currency && Object.keys(summary.receipts_amount_by_currency).length > 0 && (
        <div className="bg-zinc-950 border rounded-lg p-3 mb-5 flex flex-wrap items-center gap-3"
          style={{ borderColor: "var(--gio-border)" }} data-testid="legal-receipts-totals">
          <span className="overline">RECEIPTS TOTAL</span>
          {Object.entries(summary.receipts_amount_by_currency).map(([cur, amt]) => (
            <span key={cur} className="font-mono-ui text-[14px] font-bold" style={{ color: "var(--gio-secondary)" }}>
              {amt.toLocaleString()} {cur}
            </span>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--gio-muted-3)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, counterparty, tag…"
            data-testid="legal-search"
            className="gio-input w-full pl-8" />
        </div>
      </div>

      {/* Doc list */}
      {loading ? (
        <div className="py-12 text-center text-[13px]" style={{ color: "var(--gio-muted-2)" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-zinc-950 border rounded-lg p-12 text-center" style={{ borderColor: "var(--gio-border)" }} data-testid="legal-empty">
          <Scale size={32} className="mx-auto mb-3" style={{ color: "var(--gio-muted-3)" }} />
          <div className="font-heading text-lg" style={{ color: "var(--gio-text)" }}>
            No {TYPE_LABEL[tab]?.toLowerCase()} yet
          </div>
          <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
            {tab === "contract" ? "Click Generate Contract for an AI-drafted agreement, or Add document to upload an existing one." : "Click Add document to upload a file."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="legal-grid">
          {filtered.map(d => (
            <DocCard key={d.id} doc={d} onEdit={() => setEditing(d)} onDelete={() => removeDoc(d.id)} />
          ))}
        </div>
      )}

      {editing && (
        <DocEditor doc={editing === "new" ? { doc_type: tab } : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {generator && (
        <ContractGenerator initial={generator} onClose={() => setGenerator(null)} onSaved={() => { setGenerator(null); load(); }} />
      )}
    </AppLayout>
  );
}

function DocCard({ doc, onEdit, onDelete }) {
  const hex = TYPE_HEX[doc.doc_type] || "#71717a";
  return (
    <div className="bg-zinc-950 border rounded-lg overflow-hidden transition hover:-translate-y-0.5"
      style={{ borderColor: "var(--gio-border)" }} data-testid={`legal-doc-${doc.id}`}>
      <div className="px-4 py-3 border-b flex items-start gap-2" style={{ borderColor: "var(--gio-border)" }}>
        <div className="w-1 self-stretch rounded shrink-0" style={{ background: hex }} />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-[15px] truncate" style={{ color: "var(--gio-text)" }}>{doc.title}</div>
          {doc.counterparty && (
            <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--gio-muted-2)" }}>w/ {doc.counterparty}</div>
          )}
        </div>
        {doc.amount != null && (
          <div className="text-right shrink-0">
            <div className="font-mono-ui font-bold text-[14px]" style={{ color: "var(--gio-secondary)" }}>
              {doc.amount.toLocaleString()} {doc.currency || "EUR"}
            </div>
          </div>
        )}
      </div>
      <div className="px-4 py-2 flex items-center gap-2 flex-wrap">
        {doc.issued_date && <span className="text-[11px]" style={{ color: "var(--gio-muted-2)" }}>{doc.issued_date}</span>}
        {(doc.tags || []).slice(0, 3).map((t) => (
          <span key={t} className="text-[10px] font-mono-ui px-1.5 py-0.5 rounded"
            style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted)", border: "1px solid var(--gio-border)" }}>{t}</span>
        ))}
      </div>
      <div className="px-4 py-2 flex items-center gap-2 border-t" style={{ borderColor: "var(--gio-border)" }}>
        {doc.file_asset?.public_id ? (
          // Private vault file: fetch a short-lived (~5 min), audited link on click.
          <button type="button" data-testid={`legal-open-${doc.id}`}
            onClick={() => openPrivateFile("legal", doc.id, "file").catch(() => toast.error("Could not open file"))}
            className="text-[11px] inline-flex items-center gap-1 hover:underline"
            style={{ color: "var(--gio-secondary)" }}>
            <ExternalLink size={11} /> open (private)
          </button>
        ) : doc.file_url && (
          <a href={doc.file_url} target="_blank" rel="noreferrer"
            data-testid={`legal-open-${doc.id}`}
            className="text-[11px] inline-flex items-center gap-1 hover:underline"
            style={{ color: "var(--gio-secondary)" }}>
            <ExternalLink size={11} /> open
          </a>
        )}
        <button onClick={onEdit} type="button" data-testid={`legal-edit-${doc.id}`}
          className="text-[11px] inline-flex items-center gap-1 hover:underline ml-auto"
          style={{ color: "var(--gio-muted)" }}>
          <Edit3 size={11} /> edit
        </button>
        <button onClick={onDelete} type="button" data-testid={`legal-del-${doc.id}`}
          className="text-zinc-600 hover:text-red-400">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function DocEditor({ doc, onClose, onSaved }) {
  const isNew = !doc.id;
  const [form, setForm] = useState({
    title: doc.title || "",
    doc_type: doc.doc_type || "contract",
    counterparty: doc.counterparty || "",
    amount: doc.amount ?? "",
    currency: doc.currency || "EUR",
    issued_date: doc.issued_date || "",
    expiry_date: doc.expiry_date || "",
    file_url: doc.file_url || "",
    tags: (doc.tags || []).join(", "),
    notes: doc.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      const body = {
        ...form,
        amount: form.amount === "" ? null : Number(form.amount),
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      if (isNew) await api.post("/api/legal/", body);
      else await api.put(`/api/legal/${doc.id}`, body);
      toast.success(isNew ? "Document saved" : "Updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        data-testid="legal-editor"
        className="relative w-full max-w-2xl rounded-xl overflow-hidden flex flex-col max-h-[92vh]"
        style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <div className="overline" style={{ color: "var(--gio-secondary)" }}>{isNew ? "NEW DOCUMENT" : "EDIT DOCUMENT"}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="legal-editor-close"><XIcon size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <Field label="Title">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="legal-f-title" className="gio-input w-full" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                data-testid="legal-f-type" className="gio-input w-full">
                {TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Counterparty">
              <input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                data-testid="legal-f-counterparty" className="gio-input w-full" placeholder="Studio / editor / model" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Amount (optional)">
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="legal-f-amount" className="gio-input w-full" placeholder="0.00" />
            </Field>
            <Field label="Currency">
              <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                data-testid="legal-f-currency" className="gio-input w-full" />
            </Field>
            <Field label="Issued date">
              <input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })}
                data-testid="legal-f-date" className="gio-input w-full" />
            </Field>
          </div>
          <Field label="File URL (Cloudinary or external) — or upload PDF/image below">
            <div className="flex gap-2 items-stretch">
              <input value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })}
                data-testid="legal-f-file" className="gio-input flex-1 font-mono-ui text-xs"
                placeholder="https://res.cloudinary.com/…/raw/upload/…/contract.pdf" />
              <LegalFileUploader
                onUploaded={(secure_url) => setForm({ ...form, file_url: secure_url })}
                docType={form.doc_type}
              />
            </div>
            {form.file_url && /\.pdf($|\?)/i.test(form.file_url) && (
              <a href={form.file_url} target="_blank" rel="noreferrer"
                data-testid="legal-f-file-preview"
                className="inline-flex items-center gap-1 text-[11px] mt-1.5 underline" style={{ color: "var(--gio-primary)" }}>
                <ExternalLink size={11} /> Preview attached PDF
              </a>
            )}
          </Field>
          <Field label="Tags (comma separated)">
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
              data-testid="legal-f-tags" className="gio-input w-full" placeholder="2026, viktor-rom, signed" />
          </Field>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="legal-f-notes" className="gio-input w-full" rows={3} />
          </Field>
        </div>
        <div className="px-5 py-3 border-t flex gap-2" style={{ borderColor: "var(--gio-border)" }}>
          <button onClick={save} disabled={busy} data-testid="legal-f-save"
            className="btn-primary flex-1 disabled:opacity-50">
            {busy ? "Saving…" : (isNew ? "Save" : "Update")}
          </button>
          <button onClick={onClose} data-testid="legal-f-cancel" className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ContractGenerator({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    contract_type: initial.contract_type || "editor_hire",
    counterparty_name: "",
    counterparty_email: "",
    counterparty_country: "",
    fee_amount: 0,
    fee_currency: "EUR",
    payment_schedule: "50% upfront, 50% on delivery",
    deliverables: "",
    deadline: "",
    confidentiality: true,
    extra_terms: "",
    language: "auto",
  });
  const [generated, setGenerated] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!form.counterparty_name.trim()) return toast.error("Counterparty name required");
    setBusy(true);
    try {
      const r = await api.post("/api/legal/contract/generate", form);
      setGenerated(r.data.markdown || "");
      toast.success("Contract drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LLM error");
    } finally { setBusy(false); }
  };

  const saveAsDoc = async () => {
    if (!generated.trim()) return toast.error("Generate first");
    setBusy(true);
    try {
      const tpl = CONTRACT_TEMPLATES.find(c => c.v === form.contract_type)?.label || form.contract_type;
      // Persist as a contract doc — store the text inline in notes,
      // and the user can later upload a signed PDF and update file_url.
      await api.post("/api/legal/", {
        title: `${tpl} — ${form.counterparty_name}`,
        doc_type: form.contract_type === "model_release" ? "form" : "contract",
        counterparty: form.counterparty_name,
        amount: form.fee_amount || null,
        currency: form.fee_currency,
        issued_date: new Date().toISOString().slice(0, 10),
        notes: generated,
        tags: ["draft", form.contract_type],
      });
      toast.success("Saved as draft contract");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!generated) return;
    await navigator.clipboard.writeText(generated);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        data-testid="legal-gen"
        className="relative w-full max-w-5xl rounded-xl overflow-hidden flex flex-col max-h-[92vh]"
        style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <div className="overline inline-flex items-center gap-2" style={{ color: "var(--gio-primary)" }}>
            <Sparkles size={12} /> CONTRACT GENERATOR · XAI GROK
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="legal-gen-close"><XIcon size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 grid md:grid-cols-2 gap-5">
          {/* Inputs */}
          <div className="space-y-3" data-testid="legal-gen-inputs">
            <Field label="Contract type">
              <div className="flex flex-wrap gap-1.5">
                {CONTRACT_TEMPLATES.map(t => (
                  <button key={t.v} onClick={() => setForm({ ...form, contract_type: t.v })} type="button"
                    data-testid={`legal-gen-type-${t.v}`}
                    className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded border transition"
                    style={{
                      borderColor: form.contract_type === t.v ? "var(--gio-primary)" : "var(--gio-border)",
                      color: form.contract_type === t.v ? "var(--gio-primary)" : "var(--gio-muted)",
                      background: form.contract_type === t.v ? "var(--gio-primary-soft)" : "transparent",
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Counterparty name">
              <input value={form.counterparty_name} onChange={(e) => setForm({ ...form, counterparty_name: e.target.value })}
                data-testid="legal-gen-name" className="gio-input w-full" placeholder="e.g. Maria Editor LLC" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <input value={form.counterparty_email} onChange={(e) => setForm({ ...form, counterparty_email: e.target.value })}
                  data-testid="legal-gen-email" className="gio-input w-full" />
              </Field>
              <Field label="Country">
                <input value={form.counterparty_country} onChange={(e) => setForm({ ...form, counterparty_country: e.target.value })}
                  data-testid="legal-gen-country" className="gio-input w-full" />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Fee">
                <input type="number" value={form.fee_amount} onChange={(e) => setForm({ ...form, fee_amount: parseFloat(e.target.value) || 0 })}
                  data-testid="legal-gen-fee" className="gio-input w-full" />
              </Field>
              <Field label="Currency">
                <input value={form.fee_currency} onChange={(e) => setForm({ ...form, fee_currency: e.target.value.toUpperCase() })}
                  data-testid="legal-gen-currency" className="gio-input w-full" />
              </Field>
              <Field label="Deadline">
                <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  data-testid="legal-gen-deadline" className="gio-input w-full" />
              </Field>
            </div>
            <Field label="Payment schedule">
              <input value={form.payment_schedule} onChange={(e) => setForm({ ...form, payment_schedule: e.target.value })}
                data-testid="legal-gen-pay" className="gio-input w-full" />
            </Field>
            <Field label="Deliverables">
              <textarea value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
                data-testid="legal-gen-deliv" className="gio-input w-full" rows={3}
                placeholder="e.g. 4× edited videos (10 min each), color graded, music licensed, delivered as ProRes 4K" />
            </Field>
            <Field label="Extra terms (optional)">
              <textarea value={form.extra_terms} onChange={(e) => setForm({ ...form, extra_terms: e.target.value })}
                data-testid="legal-gen-extra" className="gio-input w-full" rows={2} />
            </Field>
            <div className="flex items-center gap-3 text-[12px]" style={{ color: "var(--gio-muted)" }}>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.confidentiality}
                  onChange={(e) => setForm({ ...form, confidentiality: e.target.checked })}
                  data-testid="legal-gen-nda" />
                Include strong NDA clause
              </label>
              <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                data-testid="legal-gen-lang"
                className="bg-black border px-2 py-1 text-[12px] rounded outline-none"
                style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}>
                <option value="auto">Auto</option>
                <option value="en">English</option>
                <option value="el">Ελληνικά</option>
              </select>
            </div>
            <button onClick={generate} disabled={busy} data-testid="legal-gen-go"
              className="btn-primary inline-flex items-center gap-2 w-full justify-center">
              <Sparkles size={13} /> {busy ? "Drafting…" : "Generate contract"}
            </button>
          </div>

          {/* Output */}
          <div className="flex flex-col" data-testid="legal-gen-output">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">DRAFT</div>
              <div className="flex items-center gap-2">
                <button onClick={copy} disabled={!generated} data-testid="legal-gen-copy"
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border disabled:opacity-30"
                  style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted)" }}>
                  <Copy size={11} /> copy
                </button>
                <button onClick={saveAsDoc} disabled={!generated || busy} data-testid="legal-gen-save"
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded font-bold uppercase disabled:opacity-30"
                  style={{ background: "var(--gio-secondary)", color: "#000" }}>
                  <DownloadIcon size={11} /> save as draft
                </button>
              </div>
            </div>
            <textarea value={generated} onChange={(e) => setGenerated(e.target.value)}
              data-testid="legal-gen-text"
              className="flex-1 bg-black border p-4 text-[12px] font-mono-ui whitespace-pre-wrap rounded outline-none resize-none"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)", minHeight: 480 }}
              placeholder={busy ? "Drafting…" : "Click Generate to draft a contract. You can tweak before saving."} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function LegalFileUploader({ onUploaded, docType }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error("File must be under 50 MB"); return; }
    const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
    setBusy(true);
    try {
      const folder = `legal/${docType || "other"}`;
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", folder);
      fd.append("resource_type", isPdf ? "raw" : "auto");
      const r = await api.post("/api/cloudinary/upload", fd);
      onUploaded(r.data.secure_url);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message || err}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" hidden accept="application/pdf,image/*"
        onChange={onFile} data-testid="legal-f-file-input" />
      <button type="button" onClick={onPick} disabled={busy}
        data-testid="legal-f-file-upload"
        className="btn-ghost text-[12px] inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60"
        title="Upload PDF or image directly">
        {busy ? <><RefreshCw size={13} className="animate-spin" /> Uploading…</> : <><DownloadIcon size={13} style={{ transform: "rotate(180deg)" }} /> Upload</>}
      </button>
    </>
  );
}

